/**
 * Flow Template Generator
 *
 * Converts TemplateNode data into Meta-compatible template JSON
 * using NAMED PARAMETERS (not indexed).
 * Exports the complete flow structure for backend integration.
 */

import { getVariableNamesFromText } from "./flowVariableValidation";

/**
 * Collect where each variable is used across exported nodes.
 * Scans text for {{var}} and explicit variable fields (selectionVariable, variableName, etc.).
 * @param {Array<{ id: string, type: string, data: Object }>} exportedNodes - Serialized nodes from export
 * @returns {Object<string, Array<{ nodeId, nodeType, field, optionalSnippet }>>} variablesUsed
 */
export function collectVariablesUsed(exportedNodes) {
  const variablesUsed = {};

  function add(nodeId, nodeType, field, variableName, snippet) {
    if (!variableName || typeof variableName !== "string") return;
    const key = variableName.trim();
    if (!key) return;
    if (!variablesUsed[key]) variablesUsed[key] = [];
    variablesUsed[key].push({
      nodeId,
      nodeType,
      field,
      ...(snippet != null && snippet !== "" && { optionalSnippet: String(snippet).slice(0, 80) }),
    });
  }

  function scanText(nodeId, nodeType, field, text) {
    if (typeof text !== "string") return;
    const names = getVariableNamesFromText(text);
    names.forEach((name) => add(nodeId, nodeType, field, name, text));
  }

  for (const node of exportedNodes || []) {
    const { id: nodeId, type: nodeType, data = {} } = node;

    // Template
    if (nodeType === "template") {
      scanText(nodeId, nodeType, "body.text", data.body?.text);
      (data.body?.variables || []).forEach((v, i) => add(nodeId, nodeType, `body.variables[${i}].name`, v.name, null));
      scanText(nodeId, nodeType, "footer.text", data.footer?.text);
      scanText(nodeId, nodeType, "header.text", data.header?.text);
      (data.buttons || []).forEach((btn, i) => {
        scanText(nodeId, nodeType, `buttons[${i}].url`, btn.url);
        if (btn.selectionVariable) add(nodeId, nodeType, `buttons[${i}].selectionVariable`, btn.selectionVariable, null);
      });
    }

    // Knowledge Base
    if (nodeType === "knowledgeBase") {
      scanText(nodeId, nodeType, "content", data.content);
    }

    // AI Ask Question
    if (nodeType === "aiAskQuestion") {
      if (data.variableName) add(nodeId, nodeType, "variableName", data.variableName, null);
      scanText(nodeId, nodeType, "question", data.question);
      (data.entities || []).forEach((e, i) => {
        if (e.variableName) add(nodeId, nodeType, `entities[${i}].variableName`, e.variableName, null);
        scanText(nodeId, nodeType, `entities[${i}].prompt`, e.prompt);
        scanText(nodeId, nodeType, `entities[${i}].fallbackMessage`, e.fallbackMessage);
      });
    }

    if (nodeType === "captureText") {
      if (data.variableName) add(nodeId, nodeType, "variableName", data.variableName, null);
      scanText(nodeId, nodeType, "question", data.question);
      scanText(nodeId, nodeType, "emptyAnswerMessage", data.emptyAnswerMessage);
    }

    // AI Intent Classifier
    if (nodeType === "aiIntentClassifier") {
      if (data.fallbackVariable) add(nodeId, nodeType, "fallbackVariable", data.fallbackVariable, null);
      scanText(nodeId, nodeType, "prompt", data.prompt);
    }

    // AI Entity Extractor
    if (nodeType === "aiEntityExtractor") {
      (data.entities || []).forEach((e, i) => {
        if (e.variableName) add(nodeId, nodeType, `entities[${i}].variableName`, e.variableName, null);
      });
    }

    // AI Response Generator
    if (nodeType === "aiResponseGenerator") {
      (data.variableNames || []).forEach((name) => add(nodeId, nodeType, "variableNames", name, null));
      if (data.storeInVariable) add(nodeId, nodeType, "storeInVariable", data.storeInVariable, null);
      scanText(nodeId, nodeType, "prompt", data.prompt);
      scanText(nodeId, nodeType, "systemPrompt", data.systemPrompt);
    }

    // AI Knowledge Base Answer
    if (nodeType === "aiKnowledgeBaseAnswer") {
      (data.contextVariableNames || []).forEach((name) => add(nodeId, nodeType, "contextVariableNames", name, null));
      if (data.storeInVariable) add(nodeId, nodeType, "storeInVariable", data.storeInVariable, null);
      scanText(nodeId, nodeType, "introMessage", data.introMessage);
      scanText(nodeId, nodeType, "assistantRole", data.assistantRole);
    }

    // Text Message
    if (nodeType === "textMessage") {
      scanText(nodeId, nodeType, "message", data.message);
    }

    // Code Transform
    if (nodeType === "codeMap" || nodeType === "jsonMap") {
      if (data.inputVariable) add(nodeId, nodeType, "inputVariable", data.inputVariable, null);
      (data.outputVariables || []).forEach((row, i) => {
        if (row.variableName) add(nodeId, nodeType, `outputVariables[${i}].variableName`, row.variableName, null);
      });
      if (data.outputVariable) add(nodeId, nodeType, "outputVariable", data.outputVariable, null);
      if (data.countVariable) add(nodeId, nodeType, "countVariable", data.countVariable, null);
      scanText(nodeId, nodeType, "code", data.code);
    }

    // Webhook
    if (nodeType === "webhook") {
      if (data.inputVariable) add(nodeId, nodeType, "inputVariable", data.inputVariable, null);
      if (data.responseVariable) add(nodeId, nodeType, "responseVariable", data.responseVariable, null);
      scanText(nodeId, nodeType, "responseMessage", data.responseMessage);
      scanText(nodeId, nodeType, "requestBody", data.requestBody);
      scanText(nodeId, nodeType, "headers", data.headers);
      (data.responseMapping || []).forEach((rm, i) => {
        if (rm.variableName) add(nodeId, nodeType, `responseMapping[${i}].variableName`, rm.variableName, null);
      });
      (data.statusHandlers || []).forEach((sh, i) => scanText(nodeId, nodeType, `statusHandlers[${i}].messageTemplate`, sh.messageTemplate));
    }

    // Interactive Buttons
    if (nodeType === "interactiveButtons") {
      if (data.selectionVariable) add(nodeId, nodeType, "selectionVariable", data.selectionVariable, null);
      scanText(nodeId, nodeType, "body.text", data.body?.text);
      scanText(nodeId, nodeType, "header.text", data.header?.text);
      scanText(nodeId, nodeType, "footer.text", data.footer?.text);
    }

    // Interactive List
    if (nodeType === "interactiveList") {
      if (data.listFromVariable) add(nodeId, nodeType, "listFromVariable", data.listFromVariable, null);
      if (data.selectionVariable) add(nodeId, nodeType, "selectionVariable", data.selectionVariable, null);
      scanText(nodeId, nodeType, "body.text", data.body?.text);
      scanText(nodeId, nodeType, "header.text", data.header?.text);
      scanText(nodeId, nodeType, "footer.text", data.footer?.text);
    }

    // Interactive URL
    if (nodeType === "interactiveUrl") {
      scanText(nodeId, nodeType, "body.text", data.body?.text);
      scanText(nodeId, nodeType, "header.text", data.header?.text);
      scanText(nodeId, nodeType, "footer.text", data.footer?.text);
      scanText(nodeId, nodeType, "url", data.url);
    }

    // Interactive Carousel URL
    if (nodeType === "interactiveCarouselUrl") {
      if (data.cardsVariable) add(nodeId, nodeType, "cardsVariable", data.cardsVariable, null);
      if (data.selectionVariable) add(nodeId, nodeType, "selectionVariable", data.selectionVariable, null);
      scanText(nodeId, nodeType, "body.text", data.body?.text);
    }

    // Interactive Carousel Button
    if (nodeType === "interactiveCarouselButton") {
      if (data.cardsVariable) add(nodeId, nodeType, "cardsVariable", data.cardsVariable, null);
      if (data.selectionVariable) add(nodeId, nodeType, "selectionVariable", data.selectionVariable, null);
      if (data.labelVariable) add(nodeId, nodeType, "labelVariable", data.labelVariable, null);
      scanText(nodeId, nodeType, "body.text", data.body?.text);
    }

    // Condition
    if (nodeType === "condition") {
      (data.conditions || []).forEach((c, i) => {
        if (c.leftValue && /^\w+$/.test(String(c.leftValue).trim())) add(nodeId, nodeType, `conditions[${i}].leftValue`, String(c.leftValue).trim(), null);
        scanText(nodeId, nodeType, `conditions[${i}].rightValue`, c.rightValue);
      });
    }

    // Controller
    if (nodeType === "controller") {
      (data.branches || []).forEach((b, i) => {
        if (b.variable) add(nodeId, nodeType, `branches[${i}].variable`, b.variable, null);
      });
    }
  }

  return variablesUsed;
}

/**
 * Generate Meta template JSON from a single TemplateNode's data.
 * Uses Meta's named-parameter format:
 *   - body_text_named_params for body variables
 *   - example array for URL button variables
 *
 * @param {Object} nodeData - The TemplateNode's data
 * @param {string} nodeId   - The node's unique ID
 * @returns {Object} Meta-compatible template JSON
 */
export function generateTemplateJSON(nodeData, nodeId) {
  const template = {
    name: `flow_node_${nodeId}`,
    language: "en_US",
    category: "MARKETING",
    components: [],
  };

  // ── HEADER ──
  const headerFormat = nodeData.header?.format || "NONE";
  if (headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: headerFormat,
    };

    if (headerFormat === "TEXT") {
      headerComponent.text = nodeData.header.text || "";
    } else {
      // IMAGE / VIDEO / DOCUMENT
      if (nodeData.header.mediaUrl) {
        headerComponent.example = {
          header_handle: [nodeData.header.mediaUrl],
        };
      }
    }

    template.components.push(headerComponent);
  }

  // ── BODY (named parameters) ──
  const bodyText = nodeData.body?.text || "";
  if (bodyText) {
    const bodyComponent = {
      type: "BODY",
      text: bodyText,
    };

    const variables = nodeData.body?.variables || [];
    if (variables.length > 0) {
      // Build body_text_named_params: single object mapping name → example
      const namedParams = {};
      variables.forEach((v) => {
        namedParams[v.name] = v.example || "";
      });
      bodyComponent.example = {
        body_text_named_params: [namedParams],
      };
    }

    template.components.push(bodyComponent);
  }

  // ── FOOTER ──
  const footerText = nodeData.footer?.text || "";
  if (footerText) {
    template.components.push({
      type: "FOOTER",
      text: footerText,
    });
  }

  // ── BUTTONS ──
  const buttons = nodeData.buttons || [];
  if (buttons.length > 0) {
    const buttonsComponent = {
      type: "BUTTONS",
      buttons: buttons.map((btn) => {
        const mapped = {
          type: btn.type,
          text: btn.text || "",
        };

        if (btn.type === "URL") {
          mapped.url = btn.url || "";

          // Parse {{variable_name}} from URL and build example array
          const urlVarMatches = (btn.url || "").match(/\{\{(\w+)\}\}/g);
          if (urlVarMatches && urlVarMatches.length > 0) {
            const varNames = urlVarMatches.map((m) => m.replace(/\{\{|\}\}/g, ""));
            const examples = btn.urlVariableExamples || {};
            mapped.example = varNames.map((name) => examples[name] || "");
          }
        }

        if (btn.type === "PHONE_NUMBER") {
          mapped.phone_number = btn.phone_number || "";
        }

        return mapped;
      }),
    };
    template.components.push(buttonsComponent);
  }

  return template;
}

/**
 * Export the complete flow as a clean JSON structure.
 *
 * @param {string} flowName
 * @param {string} flowDescription
 * @param {Array}  nodes  - ReactFlow nodes array
 * @param {Array}  edges  - ReactFlow edges array
 * @param {Array}  flowVariables - centralized variable registry
 * @returns {Object} Complete flow JSON ready for backend
 */
export function exportFlowJSON(flowName, flowDescription, nodes, edges, flowVariables) {
  const flow = {
    name: flowName || "Untitled Flow",
    description: flowDescription || "",
    variables: (flowVariables || []).map((v) => ({
      name: v.name,
      description: v.description || "",
    })),
    nodes: nodes.map((node) => {
        if (node.type === "template") {
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              templateName: node.data.templateName || "",
              templateLanguage: node.data.templateLanguage || "en_US",
              header: node.data.header,
              body: node.data.body,
              footer: node.data.footer,
              buttons: (node.data.buttons || []).map((btn) => ({
                type: btn.type,
                text: btn.text,
                id: btn.id,
                connectedNodeId: btn.connectedNodeId || null,
                selectionVariable: btn.selectionVariable || null,
                ...(btn.type === "URL" && {
                  url: btn.url,
                  urlVariableExamples: btn.urlVariableExamples || {},
                }),
                ...(btn.type === "PHONE_NUMBER" && {
                  phone_number: btn.phone_number,
                }),
              })),
              knowledgeBaseNodeId: node.data.knowledgeBaseNodeId || null,
              templateJSON: generateTemplateJSON(node.data, node.id),
              waTemplate: node.data.waTemplate || null,
            },
          };
        }

        if (node.type === "knowledgeBase") {
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: node.data.label || "Knowledge Base",
              content: node.data.content || "",
              attachments: (node.data.attachments || []).map((att) => ({
                fileName: att.fileName,
                fileUrl: att.fileUrl,
                fileType: att.fileType,
              })),
              attachedToNodeId: node.data.attachedToNodeId || null,
            },
          };
        }

        if (node.type === "aiAskQuestion") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...cleanData } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: cleanData,
          };
        }

        if (node.type === "captureText") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...cleanData } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: cleanData.label || "Ask question (text)",
              question: cleanData.question || "",
              variableName: cleanData.variableName || "selected",
              emptyAnswerMessage: cleanData.emptyAnswerMessage || "",
            },
          };
        }

        // AI Intent Classifier: include labels with connection info + history config
        if (node.type === "aiIntentClassifier") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "AI Intent Classifier",
              labels: (rest.labels || []).map((l) => ({
                id: l.id,
                value: l.value,
                connectedNodeId: l.connectedNodeId || null,
              })),
              fallbackVariable: rest.fallbackVariable || "",
              prompt: rest.prompt || "",
              historyDepth: rest.historyDepth ?? 1,
              historyMode: rest.historyMode || "user_only",
            },
          };
        }

        // AI Entity Extractor
        if (node.type === "aiEntityExtractor") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "AI Entity Extractor",
              entities: (rest.entities || []).map((e) => ({
                id: e.id,
                variableName: e.variableName,
                prompt: e.prompt,
              })),
            },
          };
        }

        // AI Response Generator
        if (node.type === "aiResponseGenerator") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "AI Response Generator",
              prompt: rest.prompt || "",
              systemPrompt: rest.systemPrompt || "",
              variableNames: rest.variableNames || [],
              knowledgeBaseNodeId: rest.knowledgeBaseNodeId || null,
              answerMode: rest.answerMode || "gpt_only",
              storeInVariable: rest.storeInVariable || "",
              sendReply: rest.sendReply !== false,
            },
          };
        }

        // AI Knowledge Base Answer
        if (node.type === "aiKnowledgeBaseAnswer") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "AI KB Answer",
              knowledgeBaseNodeId: rest.knowledgeBaseNodeId || null,
              contextVariableNames: rest.contextVariableNames || [],
              storeInVariable: rest.storeInVariable || "",
              sendReply: rest.sendReply !== undefined ? rest.sendReply : true,
              sessionMode: rest.sessionMode !== undefined ? rest.sessionMode : false,
              maxQuestions: rest.maxQuestions ?? 10,
              introMessage: rest.introMessage ?? "",
              prePromptMessagesCount: rest.prePromptMessagesCount ?? 6,
              exitOnNoAnswer: rest.exitOnNoAnswer ?? false,
              assistantRole: rest.assistantRole || "",
              answerLanguageMode: rest.answerLanguageMode || "user_language",
              answerLanguage: rest.answerLanguage || "",
            },
          };
        }

        // Text Message
        if (node.type === "textMessage") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "Text Message",
              message: rest.message || "",
            },
          };
        }

        // Webhook (with response mapping and status handlers)
        if (node.type === "webhook") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "Webhook",
              webhookUrl: rest.webhookUrl || "",
              method: rest.method || "POST",
              headers: rest.headers || "{}",
              requestBody: rest.requestBody || "",
              inputVariable: rest.inputVariable || "",
              responseVariable: rest.responseVariable || "",
              responseMessage: rest.responseMessage || "",
              responseMapping: (rest.responseMapping || []).map((rm) => ({
                id: rm.id,
                responsePath: rm.responsePath,
                variableName: rm.variableName,
              })),
              statusHandlers: (rest.statusHandlers || []).map((sh) => ({
                id: sh.id,
                statusCode: sh.statusCode,
                messageTemplate: sh.messageTemplate || "",
                connectedNodeId: sh.connectedNodeId || null,
              })),
            },
          };
        }

        // Trigger
        if (node.type === "trigger") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          const isAnyKeyword = rest.triggerOnAnyKeyword || false;
          const expectedTransferVariables = (Array.isArray(rest.expectedTransferVariables)
            ? rest.expectedTransferVariables
            : []
          )
            .map((v) => String(v).trim().replace(/^\{\{|\}\}$/g, ""))
            .filter(Boolean);
          const apiExpectedVariables =
            rest.apiExpectedVariables && typeof rest.apiExpectedVariables === "object"
              ? rest.apiExpectedVariables
              : { required: [], optional: [] };

          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: {
              label: rest.label || "Flow Trigger",
              keywords: isAnyKeyword ? ["__any__"] : (rest.keywords || []),
              triggerType: rest.triggerType || "keyword",
              triggerOnAnyKeyword: isAnyKeyword,
              allowAiHelpDuringConversation: rest.allowAiHelpDuringConversation === true,
              aiHelpKnowledgeBase: rest.aiHelpKnowledgeBase || "",
              expectedTransferVariables,
              apiExpectedVariables,
            },
          };
        }

        // Interactive Buttons
        if (node.type === "interactiveButtons") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Interactive Buttons",
              header: rest.header,
              body: rest.body,
              footer: rest.footer,
              buttons: (rest.buttons || []).map(btn => ({
                id: btn.id, label: btn.label, payload: btn.payload,
                connectedNodeId: btn.connectedNodeId || null,
              })),
              selectionVariable: rest.selectionVariable || "",
            },
          };
        }

        // Interactive List
        if (node.type === "interactiveList") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Interactive List",
              header: rest.header,
              body: rest.body,
              footer: rest.footer,
              listLabel: rest.listLabel || "Select an option",
              listSections: rest.listSections || [],
              listFromVariable: rest.listFromVariable || "",
              listVariableFieldMap: rest.listVariableFieldMap || {},
              selectionVariable: rest.selectionVariable || "",
            },
          };
        }

        // Interactive URL
        if (node.type === "interactiveUrl") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Interactive URL",
              header: rest.header,
              body: rest.body,
              footer: rest.footer,
              urlLabel: rest.urlLabel || "Visit",
              url: rest.url || "",
            },
          };
        }

        // Interactive Carousel URL
        if (node.type === "interactiveCarouselUrl") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Carousel URL",
              body: rest.body || { text: "" },
              cardsSource: rest.cardsSource || "manual",
              cards: rest.cards || [],
              cardsVariable: rest.cardsVariable || "",
              cardFieldMap: rest.cardFieldMap || {},
              selectionVariable: rest.selectionVariable || "",
            },
          };
        }

        // Interactive Carousel Button
        if (node.type === "interactiveCarouselButton") {
          const { onNodeDataChange, flowVariables: fv, onCreateVariable, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Carousel Button",
              body: rest.body || { text: "" },
              cardsSource: rest.cardsSource || "manual",
              cards: rest.cards || [],
              cardsVariable: rest.cardsVariable || "",
              cardFieldMap: rest.cardFieldMap || {},
              selectionVariable: rest.selectionVariable || "",
              labelVariable: rest.labelVariable || "",
            },
          };
        }

        // Condition node
        if (node.type === "condition") {
          const { onNodeDataChange, flowVariables: fv3, onCreateVariable: ocv2, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Condition",
              conditions: rest.conditions || [],
              logicBetween: rest.logicBetween || [],
            },
          };
        }

        // Controller node
        if (node.type === "controller") {
          const { onNodeDataChange, flowVariables: fv4, onCreateVariable: ocv3, ...rest } = node.data || {};
          return {
            id: node.id, type: node.type, position: node.position,
            data: {
              label: rest.label || "Controller",
              branches: rest.branches || [],
              hasDefault: rest.hasDefault ?? true,
            },
          };
        }

        // All other node types: strip callbacks
        const { onNodeDataChange, flowVariables: fv2, onCreateVariable: ocv, ...cleanData } = node.data || {};
        return {
          id: node.id,
          type: node.type,
          position: node.position,
          data: cleanData,
        };
      }),
    connections: edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle || null,
      conditionType: edge.data?.conditionType || "always",
      conditionValue: edge.data?.conditionValue || "",
      buttonId: edge.data?.buttonId || null,
    })),
  };

  flow.variablesUsed = collectVariablesUsed(flow.nodes);
  return { flow };
}
